import { useMemo, useRef } from "react";
import { useRecoilValue } from "recoil";

import {
  buildMapLibreRasterStyle,
  DEFAULT_TILES_SERVICE,
  googleApiKey,
  googleSession,
  mapboxAccessToken,
  systemSettingsDtoAtom,
  TilesServiceEnum,
} from "@ryde/shared";

/**
 * Raster style for the MapLibre map. `tilesServiceOverride` is the client-side
 * layer-control choice (Google/Mapbox/OSM); it wins over the company default so
 * switching layers rebuilds the raster source.
 */
export const useMapLibreRasterStyle = (
  tilesServiceOverride?: TilesServiceEnum,
) => {
  const systemSettings = useRecoilValue(systemSettingsDtoAtom);
  const sessionObj = useRecoilValue(googleSession);
  const lastGoogleSessionTokenRef = useRef<string | undefined>(undefined);

  if (sessionObj?.session) {
    lastGoogleSessionTokenRef.current = sessionObj.session;
  }

  // Keep the last known Google session while a refresh is in flight — clearing the atom to null used to
  // rebuild every map's raster style twice (Google → OSM → Google) and race marker cleanup against destroy.
  const googleSessionToken =
    sessionObj?.session ?? lastGoogleSessionTokenRef.current;

  return useMemo(() => {
    const tilesService =
      tilesServiceOverride ||
      systemSettings?.metadata?.tilesService ||
      DEFAULT_TILES_SERVICE;

    return buildMapLibreRasterStyle({
      tilesService,
      googleSessionToken,
      googleApiKeyValue: googleApiKey(),
      mapboxToken: mapboxAccessToken() ?? undefined,
    });
  }, [
    googleSessionToken,
    systemSettings?.metadata?.tilesService,
    tilesServiceOverride,
  ]);
};
